import { useState } from 'react';
import { getPose, usePoses } from '../data/poses';
import {
  DEFAULT_HOLD_SECONDS,
  formatDuration,
  stepIsBilateral,
  type RoutinesApi,
} from '../hooks/useRoutines';
import { navigate } from '../router';
import type { RoutineStep } from '../types';

interface Props {
  routines: RoutinesApi;
  routineId?: string;
  /** Pre-fill a brand-new routine (e.g. from the AI generator) for editing. */
  initialName?: string;
  initialSteps?: RoutineStep[];
}

// Common holds. 30s ≈ five slow breaths, our default "hold for five breaths".
const HOLD_PRESETS = [15, 30, 45, 60];

export function RoutineBuilderPage({ routines, routineId, initialName, initialSteps }: Props) {
  const poses = usePoses();
  const existing = routineId ? routines.get(routineId) : undefined;
  const [name, setName] = useState(existing?.name ?? initialName ?? '');
  const [steps, setSteps] = useState<RoutineStep[]>(existing?.steps ?? initialSteps ?? []);

  if (routineId && !existing) {
    return (
      <div className="empty">
        <p>Routine not found.</p>
        <a className="btn" href="#/routines">
          ← Back to routines
        </a>
      </div>
    );
  }

  const addPose = (poseId: string) =>
    setSteps((prev) => [
      ...prev,
      { poseId, holdSeconds: DEFAULT_HOLD_SECONDS, bilateral: !!getPose(poseId)?.bilateral },
    ]);

  const removeStep = (i: number) =>
    setSteps((prev) => prev.filter((_, idx) => idx !== i));

  const move = (i: number, dir: -1 | 1) =>
    setSteps((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const setHold = (i: number, holdSeconds: number) =>
    setSteps((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, holdSeconds: Math.max(1, holdSeconds) } : s)),
    );

  const setBilateral = (i: number, bilateral: boolean) =>
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, bilateral } : s)));

  const total = steps.reduce(
    (sum, s) => sum + (s.holdSeconds || 0) * (stepIsBilateral(s) ? 2 : 1),
    0,
  );

  const save = () => {
    if (steps.length === 0) return;
    if (existing) routines.update(existing.id, { name, steps });
    else routines.create(name, steps);
    navigate('/routines');
  };

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">{existing ? 'Edit routine' : 'New routine'}</h1>
        <a className="back" href="#/routines">
          ← Routines
        </a>
      </div>

      <input
        className="name-input"
        type="text"
        placeholder="Routine name (e.g. Morning Wake-Up)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <div className="builder">
        <section className="builder-col">
          <h2 className="section-label">Add poses</h2>
          <ul className="pose-picker">
            {poses.map((p) => (
              <li key={p.id}>
                <span className="pp-name">
                  {p.nameEnglish}
                  {p.nameSanskrit && <em> · {p.nameSanskrit}</em>}
                </span>
                <button type="button" className="btn sm" onClick={() => addPose(p.id)}>
                  + Add
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="builder-col">
          <h2 className="section-label">
            Your sequence{steps.length > 0 && ` · ${formatDuration(total)}`}
          </h2>
          {steps.length === 0 ? (
            <p className="empty small">Add poses to build your sequence.</p>
          ) : (
            <ol className="sequence">
              {steps.map((step, i) => {
                const pose = getPose(step.poseId);
                return (
                  <li className="seq-row" key={`${step.poseId}-${i}`}>
                    <span className="seq-num">{i + 1}</span>
                    <span className="seq-name">{pose?.nameEnglish ?? step.poseId}</span>
                    <div className="seq-hold">
                      <span className="seq-hold-label">Hold</span>
                      <div className="hold-presets">
                        {HOLD_PRESETS.map((p) => (
                          <button
                            key={p}
                            type="button"
                            className={`chip${step.holdSeconds === p ? ' on' : ''}`}
                            onClick={() => setHold(i, p)}
                          >
                            {p}s
                          </button>
                        ))}
                      </div>
                      <label className="hold-custom" title="Custom hold in seconds">
                        <input
                          type="number"
                          min={1}
                          value={step.holdSeconds}
                          onChange={(e) => setHold(i, Number(e.target.value))}
                        />
                        s
                      </label>
                      <button
                        type="button"
                        className={`chip${stepIsBilateral(step) ? ' on' : ''}`}
                        onClick={() => setBilateral(i, !stepIsBilateral(step))}
                        title="Do this pose on both sides (adds an 'other side' round)"
                      >
                        ⇆ Both sides
                      </button>
                    </div>
                    <span className="seq-controls">
                      <button
                        type="button"
                        className="icon"
                        aria-label="Move up"
                        disabled={i === 0}
                        onClick={() => move(i, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="icon"
                        aria-label="Move down"
                        disabled={i === steps.length - 1}
                        onClick={() => move(i, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="icon danger"
                        aria-label="Remove"
                        onClick={() => removeStep(i)}
                      >
                        ✕
                      </button>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>

      <div className="builder-actions">
        <a className="btn" href="#/routines">
          Cancel
        </a>
        <button type="button" className="btn primary" disabled={steps.length === 0} onClick={save}>
          {existing ? 'Save changes' : 'Save routine'}
        </button>
      </div>
    </>
  );
}
